import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarClock, Plus, Repeat, Edit, Trash2, Clock } from "lucide-react";

const DAYS = [
  { v: 0, l: "Seg" }, { v: 1, l: "Ter" }, { v: 2, l: "Qua" }, { v: 3, l: "Qui" },
  { v: 4, l: "Sex" }, { v: 5, l: "Sáb" }, { v: 6, l: "Dom" },
];
const VARS = ["{nome}", "{whatsapp}", "{email}"];

const EMPTY = {
  name: "", message: "", audience_type: "all", audience_value: null,
  days: [], time: "09:00", interval_min: 15, interval_max: 40, max_messages: null, active: true,
};

export default function RecurringSection({ tags, templates }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/recurring").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (d) => setForm((f) => ({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.message.trim()) return toast.error("Preencha nome e mensagem.");
    if (form.days.length === 0) return toast.error("Selecione ao menos um dia da semana.");
    setSaving(true);
    try {
      const payload = { ...form, max_messages: form.max_messages ? parseInt(form.max_messages) : null };
      if (editing) await api.put(`/recurring/${editing.id}`, payload);
      else await api.post("/recurring", payload);
      toast.success("Agendamento salvo!");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (r) => { await api.patch(`/recurring/${r.id}/toggle`, { active: !r.active }); load(); };
  const remove = async (id) => { await api.delete(`/recurring/${id}`); toast.success("Agendamento removido."); load(); };

  return (
    <div className="mt-10" data-testid="recurring-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-emerald-50 flex items-center justify-center"><Repeat className="h-5 w-5 text-emerald-600" /></div>
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">Agendamentos automáticos</h2>
            <p className="text-xs text-slate-500">Programe dias e horário para os disparos rodarem sozinhos, sem recriar campanhas.</p>
          </div>
        </div>
        <Button onClick={openNew} variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="recurring-new-button">
          <Plus className="h-4 w-4 mr-2" /> Novo agendamento
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.length === 0 && <p className="text-sm text-slate-400 col-span-2 text-center py-8 bg-white rounded-xl border border-dashed border-slate-200">Nenhum agendamento. Crie um para enviar automaticamente em dias fixos.</p>}
        {items.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-soft" data-testid={`recurring-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-emerald-600" />
                <h3 className="font-semibold text-slate-800">{r.name}</h3>
              </div>
              <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} data-testid={`recurring-toggle-${r.id}`} />
            </div>
            <div className="flex flex-wrap gap-1 mt-3">
              {DAYS.map((d) => (
                <span key={d.v} className={`text-[10px] px-2 py-0.5 rounded-full border ${r.days?.includes(d.v) ? "bg-emerald-500 text-white border-emerald-500" : "bg-slate-50 text-slate-400 border-slate-200"}`}>{d.l}</span>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {r.time}</span>
              <span>Intervalo {r.interval_min}-{r.interval_max}s</span>
              {r.max_messages && <span>Máx {r.max_messages}</span>}
            </div>
            <p className="text-xs text-slate-400 mt-2 line-clamp-2">{r.message}</p>
            {r.last_run && <p className="text-[11px] text-emerald-600 mt-2">Último envio: {r.last_run}{r.last_count != null ? ` (${r.last_count} contatos)` : ""}</p>}
            <div className="flex gap-1 mt-3 pt-3 border-t border-slate-100">
              <Button size="sm" variant="ghost" onClick={() => openEdit(r)} className="text-slate-600" data-testid={`recurring-edit-${r.id}`}><Edit className="h-4 w-4 mr-1" /> Editar</Button>
              <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-red-500 ml-auto"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="recurring-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Editar agendamento" : "Novo agendamento automático"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nome do agendamento</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex: Lembrete semanal" className="mt-1.5" data-testid="recurring-name-input" /></div>

            <div>
              <Label>Dias da semana</Label>
              <div className="flex flex-wrap gap-2 mt-1.5" data-testid="recurring-days">
                {DAYS.map((d) => (
                  <button key={d.v} type="button" onClick={() => toggleDay(d.v)} data-testid={`recurring-day-${d.v}`}
                    className={`h-10 w-12 rounded-lg text-sm font-medium border transition-colors ${form.days.includes(d.v) ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>{d.l}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Horário</Label><Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className="mt-1.5" data-testid="recurring-time-input" /></div>
              <div><Label>Intervalo mín. (s)</Label><Input type="number" min={5} value={form.interval_min} onChange={(e) => set("interval_min", parseInt(e.target.value) || 0)} className="mt-1.5" /></div>
              <div><Label>Intervalo máx. (s)</Label><Input type="number" min={5} value={form.interval_max} onChange={(e) => set("interval_max", parseInt(e.target.value) || 0)} className="mt-1.5" /></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Destinatários</Label>
                <Select value={form.audience_type} onValueChange={(v) => set("audience_type", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="recurring-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os clientes</SelectItem>
                    <SelectItem value="tag">Por tag</SelectItem>
                    <SelectItem value="status">Por status</SelectItem>
                    <SelectItem value="recent">Importados recentemente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Máx. mensagens (opcional)</Label><Input type="number" value={form.max_messages || ""} onChange={(e) => set("max_messages", e.target.value)} placeholder="Sem limite" className="mt-1.5" /></div>
            </div>

            {form.audience_type === "tag" && (
              <div><Label>Tag</Label>
                <Select value={form.audience_value || ""} onValueChange={(v) => set("audience_value", v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Selecione uma tag" /></SelectTrigger>
                  <SelectContent>{tags.map((t) => <SelectItem key={t.id} value={t.name}>{t.name} ({t.count})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.audience_type === "status" && (
              <div><Label>Status</Label>
                <Select value={form.audience_value || ""} onValueChange={(v) => set("audience_value", v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent><SelectItem value="ATIVO">Ativo</SelectItem><SelectItem value="INATIVO">Inativo</SelectItem><SelectItem value="PROSPECTO">Prospecto</SelectItem></SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Mensagem</Label>
              <div className="flex gap-2 flex-wrap my-1.5">
                {templates.map((t) => <button key={t.id} type="button" onClick={() => set("message", t.content)} className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700">{t.name}</button>)}
              </div>
              <Textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={5} placeholder="Olá {nome}! ..." data-testid="recurring-message-input" />
              <div className="flex gap-2 mt-2">{VARS.map((v) => <button key={v} type="button" onClick={() => set("message", (form.message || "") + v)} className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 font-mono border border-emerald-100">{v}</button>)}</div>
            </div>

            {!editing && (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} /> Ativar imediatamente
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600" data-testid="recurring-save-button">{saving ? "Salvando..." : "Salvar agendamento"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
